import { apiData, apiRequest } from "./http.js";

function mapPath(mapId) {
  return `/api/workshop/maps/${encodeURIComponent(mapId)}`;
}

function levelPath(mapId, levelNo) {
  return `${mapPath(mapId)}/levels/${encodeURIComponent(levelNo)}`;
}

function optionalShareBody(shareCode) {
  const value = String(shareCode || "").trim();
  return value ? { shareCode: value } : {};
}

export async function uploadImage(file) {
  if (!(file instanceof Blob)) throw new TypeError("Workshop image must be a Blob or File.");
  const form = new FormData();
  form.append("file", file, file instanceof File ? file.name : "workshop-image");
  const uploaded = apiData(await apiRequest("/api/workshop/assets/images", {
    method: "POST",
    body: form
  }));
  return {
    ...uploaded,
    imageUrl: String(uploaded?.imageUrl || uploaded?.image_url || uploaded?.url || ""),
    storageKey: String(uploaded?.storageKey || uploaded?.storage_key || uploaded?.key || "")
  };
}

export async function listPublicMaps({ cursor = "", limit = 20 } = {}) {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", String(cursor));
  params.set("limit", String(limit));
  // Always fetch the current publication state after a creator changes a map
  // between private and public; some embedded mobile browsers cache GET lists.
  params.set("refresh", String(Date.now()));
  const query = params.toString();
  return apiData(await apiRequest(`/api/workshop/public-maps${query ? `?${query}` : ""}`));
}

export async function recordPublicMapView(mapId) {
  return apiData(await apiRequest(
    `/api/workshop/public-maps/${encodeURIComponent(mapId)}/view`,
    { method: "POST" }
  ));
}

export async function likePublicMap(mapId) {
  return apiData(await apiRequest(
    `/api/workshop/public-maps/${encodeURIComponent(mapId)}/like`,
    { method: "POST" }
  ));
}

export async function unlikePublicMap(mapId) {
  return apiData(await apiRequest(
    `/api/workshop/public-maps/${encodeURIComponent(mapId)}/like`,
    { method: "DELETE" }
  ));
}

export async function reportPublicMap(mapId, payload = {}) {
  return apiData(await apiRequest(
    `/api/workshop/public-maps/${encodeURIComponent(mapId)}/report`,
    { method: "POST", body: payload }
  ));
}

export async function listModerationQueue(status = "pending") {
  return apiData(await apiRequest(`/api/admin/workshop/moderation?status=${encodeURIComponent(status)}`));
}

export async function listModerationAudit() {
  return apiData(await apiRequest("/api/admin/moderation/audit"));
}

export async function moderateMap(mapId, decision, reason = "") {
  return apiData(await apiRequest(`/api/admin/workshop/maps/${encodeURIComponent(mapId)}/moderation`, {
    method: "PATCH",
    body: { decision, reason }
  }));
}

export async function getWorkshopSafetyStatus(){ return apiData(await apiRequest("/api/workshop/safety-status")); }
export async function submitMapAppeal(mapId,message){ return apiData(await apiRequest(`${mapPath(mapId)}/appeals`,{method:"POST",body:{message}})); }
export async function listAppeals(status="pending"){ return apiData(await apiRequest(`/api/admin/appeals?status=${encodeURIComponent(status)}`)); }
export async function resolveAppeal(id,decision,resolution=""){ return apiData(await apiRequest(`/api/admin/appeals/${encodeURIComponent(id)}`,{method:"PATCH",body:{decision,resolution}})); }

export async function getPublicMap(mapId) {
  return apiData(await apiRequest(`/api/workshop/public-maps/${encodeURIComponent(mapId)}`));
}

export async function listMyMaps() {
  return apiData(await apiRequest(`/api/workshop/maps?refresh=${Date.now()}`));
}

export async function getMap(mapId) {
  return apiData(await apiRequest(mapPath(mapId)));
}

export async function createMap(payload) {
  return apiData(await apiRequest("/api/workshop/maps", {
    method: "POST",
    body: payload
  }));
}

export async function updateMap(mapId, payload) {
  return apiData(await apiRequest(mapPath(mapId), {
    method: "PATCH",
    body: payload
  }));
}

export async function deleteMap(mapId) {
  return apiData(await apiRequest(mapPath(mapId), {
    method: "DELETE"
  }));
}

export async function getLevel(mapId, levelNo) {
  return apiData(await apiRequest(levelPath(mapId, levelNo)));
}

export async function saveLevel(mapId, levelNo, payload) {
  return apiData(await apiRequest(levelPath(mapId, levelNo), {
    method: "PUT",
    body: payload
  }));
}

export async function deleteLevel(mapId, levelNo) {
  return apiData(await apiRequest(levelPath(mapId, levelNo), {
    method: "DELETE"
  }));
}

export async function createShareLink(mapId) {
  return apiData(await apiRequest(`${mapPath(mapId)}/share-link`, {
    method: "POST"
  }));
}

export async function deleteShareLink(mapId) {
  return apiData(await apiRequest(`${mapPath(mapId)}/share-link`, {
    method: "DELETE"
  }));
}

export async function getSharedMap(shareCode) {
  return apiData(await apiRequest(
    `/api/workshop/shared/${encodeURIComponent(shareCode)}`,
    { skipAuthBootstrap: true }
  ));
}

export async function getChallengeMap() {
  return apiData(await apiRequest("/api/workshop/challenge-map"));
}

export async function setChallengeMap(mapId = null) {
  return apiData(await apiRequest("/api/workshop/challenge-map", {
    method: "PATCH",
    body: { mapId: mapId ? String(mapId) : null }
  }));
}

export async function getProgress(mapId, { shareCode = "" } = {}) {
  const params = new URLSearchParams();
  if (shareCode) params.set("shareCode", String(shareCode));
  const query = params.toString();
  return apiData(await apiRequest(`${mapPath(mapId)}/progress${query ? `?${query}` : ""}`));
}

export async function startCustomLevel(mapId, levelNo, { shareCode = "" } = {}) {
  return apiData(await apiRequest(`${levelPath(mapId, levelNo)}/start`, {
    method: "POST",
    body: optionalShareBody(shareCode)
  }));
}

export async function finishCustomLevel(mapId, levelNo, payload) {
  return apiData(await apiRequest(`${levelPath(mapId, levelNo)}/finish`, {
    method: "POST",
    body: payload
  }));
}
